import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ChevronLeft, Receipt, DollarSign, Info, Tag, AlertCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import TextArea from '../components/ui/TextArea';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchCurrencies } from '../apiHelper';

const client = new FastAPIClient();

const EditVendorGoodsReceiptWithoutPayment = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();

    // The state now contains the full 'items' array from VendorPurchaseOrders
    const editData = location.state;
    const vendorId = editData?.vendor_id;
    const vendorName = editData?.vendor_name || 'Selected Vendor';
    const purchaseorder_id = editData?.purchaseorder_id;

    const [user, setUser] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(editData?.currency || 'NGN');
    const [loadingData, setLoadingData] = useState(true);

    // This is our main form state
    const [receivedItems, setReceivedItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to edit receipts.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    useEffect(() => {
        const loadInitialData = async () => {
            if (!user) return;
            setLoadingData(true);
            try {
                const fetchedCurrencies = await fetchCurrencies(user);
                const currencyOptions = fetchedCurrencies.map(curr => ({
                    label: `${curr.setccurr} (${curr.setcnote})`,
                    value: curr.setccurr,
                }));
                setCurrencies(currencyOptions);

                // HYDRATION: Map the items sent via location.state directly into the form
                if (editData?.items) {
                    const hydratedItems = editData.items.map(item => ({
                        catentry_id: item.catentry_id,
                        productname: item.productname,
                        partnumber: item.partnumber,
                        qtyordered: item.qtyordered,
                        // Form inputs
                        qtyreceived_input: item.qtyreceived || '',
                        // cost: item.cost || '',
                        // listprice: item.listprice || '',
                        comment: item.comment || '',
                        goodsreceipt_id: item.goodsreceipt_id
                    }));
                    setReceivedItems(hydratedItems);
                }
            } catch (err) {
                showToast("Failed to load currencies.", "error");
            } finally {
                setLoadingData(false);
            }
        };
        loadInitialData();
    }, [user, editData]);

    const handleReceivedItemChange = useCallback((catentryId, field, value) => {
        setReceivedItems(prev => prev.map(item =>
            item.catentry_id === catentryId ? { ...item, [field]: value } : item
        ));
    }, []);

    const validateForm = () => {
        let errors = {};
        const hasValidEntry = receivedItems.some(item => parseFloat(item.qtyreceived_input) > 0);
        if (!hasValidEntry) errors.general = "At least one item must have a quantity greater than 0.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmitReceipt = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);

        try {
            const payload = {
                purchaseorder_id: purchaseorder_id,
                vendor_id: vendorId,
                currency: selectedCurrency,
                // Filter out items with no quantity recorded
                items: receivedItems
                    .filter(item => parseFloat(item.qtyreceived_input) > 0)
                    .map(item => ({
                        goodsreceipt_id: item.goodsreceipt_id,
                        catentry_id: item.catentry_id,
                        qtyreceived: parseFloat(item.qtyreceived_input),
                        // cost: parseFloat(item.cost),
                        // listprice: parseFloat(item.listprice),
                        comment: item.comment
                    }))
            };

            await client.postData('/vendor/edit-goods-receipt', payload);
            showToast("Goods Receipt updated successfully", "success");
            navigate(-1);
        } catch (err) {
            showToast("Failed to update receipt.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loadingData) return (
        <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
    );

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
            <Card className="max-w-5xl mx-auto p-6 shadow-xl border-t-4 border-blue-600">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Receipt className="text-blue-600" /> Edit Goods Receipt
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">PO: {editData?.ponumber} | Vendor: {vendorName}</p>
                    </div>
                    <Button onClick={() => navigate(-1)} variant="secondary" size="sm">
                        <ChevronLeft size={18} className="mr-1" /> Back
                    </Button>
                </div>

                <div className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SearchableSelectBox
                        label="Transaction Currency"
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                        options={currencies}
                        icon={<DollarSign size={18} />}
                    />
                    <div className="flex items-center gap-3 text-blue-800">
                        <Info size={20} />
                        <p className="text-xs leading-tight">
                            Modifying these quantities will update the inventory levels and outstanding vendor balance once approved.
                        </p>
                    </div>
                </div>

                {formErrors.general && (
                    <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
                        <AlertCircle size={18} /> {formErrors.general}
                    </div>
                )}

                <div className="space-y-4">
                    {receivedItems.map((item) => (
                        <div key={item.catentry_id} className="p-5 border border-slate-200 rounded-xl bg-white hover:border-blue-300 transition-colors shadow-sm">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="font-bold text-slate-800">{item.productname}</h4>
                                    <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded text-slate-500 font-mono">{item.partnumber}</span>
                                    {/* <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md flex items-center gap-1">
                                        <Tag size={12} />
                                        Current System Price: {selectedCurrency} {item.listprice?.toLocaleString() || '0.00'}
                                    </span> */}
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] uppercase text-slate-400 font-bold">Ordered</p>
                                    <p className="font-bold text-slate-700">{item.qtyordered}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input
                                    label="Quantity Received"
                                    type="number"
                                    value={item.qtyreceived_input}
                                    onChange={(e) => handleReceivedItemChange(item.catentry_id, 'qtyreceived_input', e.target.value)}
                                    className="border-slate-300 focus:ring-blue-500"
                                />
                                {/* <Input
                                    label={`Unit Cost (${selectedCurrency})`}
                                    type="number"
                                    value={item.cost}
                                    onChange={(e) => handleReceivedItemChange(item.catentry_id, 'cost', e.target.value)}
                                    className="border-slate-300 focus:ring-blue-500"
                                />
                                <Input
                                    label={`Selling Price (${selectedCurrency})`}
                                    type="number"
                                    value={item.listprice}
                                    onChange={(e) => handleReceivedItemChange(item.catentry_id, 'listprice', e.target.value)}
                                    className="border-slate-300 focus:ring-blue-500"
                                /> */}
                            </div>
                            <div className="mt-4">
                                <TextArea
                                    label="Item Note"
                                    placeholder="Discrepancies, batch numbers, or quality remarks..."
                                    value={item.comment}
                                    onChange={(e) => handleReceivedItemChange(item.catentry_id, 'comment', e.target.value)}
                                    rows={1}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 pt-6 border-t border-slate-200 flex justify-end">
                    <Button
                        onClick={handleSubmitReceipt}
                        variant="primary"
                        size="lg"
                        className="px-12 py-4 text-lg font-bold shadow-lg shadow-blue-200"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <><Loader2 className="animate-spin mr-2" /> UPDATING...</>
                        ) : 'SAVE CHANGES'}
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EditVendorGoodsReceiptWithoutPayment;